/**
 * Hook to build API request headers with auth token and team context.
 * Usage:
 *   const { getHeaders } = useApiHeaders();
 *   const headers = await getHeaders();
 *   fetch(url, { headers });
 *
 * Prefers the internal cloud JWT (contains team_id, faster server-side
 * verification) over Firebase ID tokens. Falls back to Firebase token
 * if no cloud JWT is stored (e.g. before first sync completes).
 *
 * Team ID is read from localStorage (not React state) to avoid stale
 * closures in useEffect callbacks and setInterval handlers.
 */

import { useAuth } from '../contexts/AuthContext';
import { getSelectedTeamId } from '../services/api';

export function useApiHeaders() {
  const { firebaseUser, cloudToken } = useAuth();

  const getHeaders = async (extra?: Record<string, string>): Promise<Record<string, string>> => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...extra,
    };

    // Prefer cloud JWT (has team_id, fast server-side verify)
    if (cloudToken) {
      headers['Authorization'] = `Bearer ${cloudToken}`;
    } else if (firebaseUser) {
      // Fallback to Firebase ID token (before sync completes)
      const token = await firebaseUser.getIdToken();
      headers['Authorization'] = `Bearer ${token}`;
    }

    const teamId = getSelectedTeamId();
    if (teamId) {
      headers['X-Team-ID'] = teamId;
    }

    return headers;
  };

  return { getHeaders };
}
